//! Page geometry for the Page Break Preview.
//!
//! Holds the current sheet's pagination as computed by the core (the same
//! pagination PDF export uses) and converts it to world coordinates for the
//! overlay and the drag handler. Refetched whenever anything that moves a page
//! boundary changes: the print setup, row/column sizes, or the sheet's content
//! bounds.

import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import type { JsSheetPagination } from '@/app/quadratic-core-types';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { Rectangle } from 'pixi.js';

export interface PageBreakLine {
  /** The 1-based track that starts the page after this line. */
  index: number;
  /** World coordinate of the line. */
  position: number;
  manual: boolean;
}

export interface PageRect {
  rect: Rectangle;
  pageNumber: number;
}

/** The line shown while a break or the print-area boundary is being dragged. */
export interface PageBreakDragPreview {
  vertical: boolean;
  /** 1-based track the line sits in front of. */
  boundary: number;
}

class PageBreaks {
  private pagination: JsSheetPagination | undefined;
  private paginationSheetId: string | undefined;
  /** Sheet the last request was made for, whether or not it succeeded. */
  private requestedSheetId: string | undefined;
  private fetching = false;
  private refetchWhenDone = false;
  private _preview: PageBreakDragPreview | undefined;

  constructor() {
    events.on('sheetPrintSetup', this.onSheetChanged);
    events.on('sheetOffsetsUpdated', this.onSheetChanged);
    events.on('changeSheet', this.refresh);
    events.on('gridSettings', this.refresh);
    events.on('sheetBounds', this.refresh);
    events.on('sheetsInfo', this.refresh);
  }

  get enabled(): boolean {
    const settings = gridSettings.getGridSettings();
    return settings.pageBreakPreview && !settings.presentationMode;
  }

  /** Pagination for the current sheet, or undefined when it has nothing to print. */
  get current(): JsSheetPagination | undefined {
    if (!this.enabled || this.paginationSheetId !== sheets.current) return undefined;
    return this.pagination;
  }

  get preview(): PageBreakDragPreview | undefined {
    return this._preview;
  }

  set preview(preview: PageBreakDragPreview | undefined) {
    this._preview = preview;
    events.emit('pageBreaks');
  }

  /** World coordinate of the drag preview line, on its own axis. */
  getPreviewPosition(): number | undefined {
    if (!this._preview) return undefined;
    const offsets = sheets.sheet.offsets;
    const { vertical, boundary } = this._preview;
    return vertical ? offsets.getColumnPlacement(boundary).position : offsets.getRowPlacement(boundary).position;
  }

  private onSheetChanged = (sheetId: string) => {
    if (sheetId === sheets.current) this.refresh();
  };

  /**
   * Fetches pagination when the current sheet has none. Startup order isn't
   * guaranteed — an import cycle can leave this class subscribed to
   * `sheetsInfo` ahead of `Sheets`, so the first refresh runs before there is a
   * current sheet — and the setting persists across reloads, so the overlay
   * asks for what it needs each frame rather than trusting an event it may
   * have already missed.
   */
  ensurePagination = () => {
    if (this.enabled && sheets.initialized && this.requestedSheetId !== sheets.current) {
      this.refresh();
    }
  };

  refresh = () => {
    if (!this.enabled) {
      this.requestedSheetId = undefined;
      if (this.pagination !== undefined) {
        this.pagination = undefined;
        this.paginationSheetId = undefined;
        events.emit('pageBreaks');
      }
      return;
    }

    // Nothing to paginate until the sheets exist; `ensurePagination` picks
    // this back up on the first frame after they do.
    if (!sheets.initialized) return;

    // Only one request in flight; anything that lands while fetching is
    // collapsed into a single follow-up.
    if (this.fetching) {
      this.refetchWhenDone = true;
      return;
    }
    this.fetching = true;

    const sheetId = sheets.current;
    this.requestedSheetId = sheetId;
    quadraticCore
      .getSheetPagination(sheetId)
      .then((pagination) => {
        this.pagination = pagination;
        this.paginationSheetId = sheetId;
        events.emit('pageBreaks');
      })
      .catch((error) => console.error('[pageBreaks] failed to get pagination', error))
      .finally(() => {
        this.fetching = false;
        if (this.refetchWhenDone) {
          this.refetchWhenDone = false;
          this.refresh();
        }
      });
  };

  /**
   * World rect of everything that prints, or undefined when nothing does. This
   * covers the print titles repeated on every page, which can sit outside the
   * print area, so it is the region to leave unshaded rather than the region a
   * print-area drag acts on (see `getPrintAreaRect`).
   */
  getPrintRect(): Rectangle | undefined {
    const pagination = this.current;
    if (!pagination) return undefined;
    return this.worldRect(Number(pagination.min_x), Number(pagination.min_y), pagination);
  }

  /**
   * World rect of the print area itself, which is what the heavy border marks
   * and what dragging an edge resizes. Print titles outside it are not part of
   * it, so resizing must not absorb them.
   */
  getPrintAreaRect(): Rectangle | undefined {
    const pagination = this.current;
    if (!pagination) return undefined;
    return this.worldRect(Number(pagination.print_area_min_x), Number(pagination.print_area_min_y), pagination);
  }

  /** A rect from `minX`/`minY` to the pagination's bottom-right track. */
  private worldRect(minX: number, minY: number, pagination: JsSheetPagination): Rectangle {
    const offsets = sheets.sheet.offsets;
    const left = offsets.getColumnPlacement(minX).position;
    const top = offsets.getRowPlacement(minY).position;
    const right = offsets.getColumnPlacement(Number(pagination.max_x) + 1).position;
    const bottom = offsets.getRowPlacement(Number(pagination.max_y) + 1).position;
    return new Rectangle(left, top, right - left, bottom - top);
  }

  /** Vertical break lines, one per column band after the first. */
  getColumnBreaks(): PageBreakLine[] {
    const pagination = this.current;
    if (!pagination) return [];
    const offsets = sheets.sheet.offsets;
    return pagination.col_bands.slice(1).map((band) => ({
      index: Number(band.start),
      position: offsets.getColumnPlacement(Number(band.start)).position,
      manual: band.manual,
    }));
  }

  /** Horizontal break lines, one per row band after the first. */
  getRowBreaks(): PageBreakLine[] {
    const pagination = this.current;
    if (!pagination) return [];
    const offsets = sheets.sheet.offsets;
    return pagination.row_bands.slice(1).map((band) => ({
      index: Number(band.start),
      position: offsets.getRowPlacement(Number(band.start)).position,
      manual: band.manual,
    }));
  }

  /** World rect and page number of every page, for the `Page N` watermarks. */
  getPageRects(): PageRect[] {
    const pagination = this.current;
    if (!pagination) return [];
    const offsets = sheets.sheet.offsets;

    return pagination.pages.map(({ col_band, row_band, page_number }) => {
      const cols = pagination.col_bands[col_band];
      const rows = pagination.row_bands[row_band];
      const left = offsets.getColumnPlacement(Number(cols.start)).position;
      const right = offsets.getColumnPlacement(Number(cols.end) + 1).position;
      const top = offsets.getRowPlacement(Number(rows.start)).position;
      const bottom = offsets.getRowPlacement(Number(rows.end) + 1).position;
      return {
        rect: new Rectangle(left, top, right - left, bottom - top),
        pageNumber: page_number,
      };
    });
  }
}

export const pageBreaks = new PageBreaks();
